import ApiClient from '@/api/ApiClient';
import BaseService from './BaseService';
import Material from '@/api/models/Material';
import SellingMaterialGroup from '@/api/models/SellingMaterialGroup';
import SellingMaterial from '@/api/models/SellingMaterial';

class SellingMaterialService extends BaseService {
    #prefixUrl = 'api/selling_material/';

    STORAGE_DEFINITION = {
        MATERIAL_LIST: 'sellingMaterialList',
    };

    constructor() {
        super();
        this.api = new ApiClient(this.#prefixUrl);
    }

    /**
     * Get all SellingMaterialGroup of User
     * @returns {Array}
     */
    async get() {
        let sellingMaterialGroups = [];

        const response = await this.api.executeRequest(`/`, {}, this.api.methods.GET);

        if (response.success() && response.getContent() != null) {
            for (let sellingMaterialGroup of response.getContent()) {
                sellingMaterialGroups.push(new SellingMaterialGroup(sellingMaterialGroup));
            }
        }

        return sellingMaterialGroups;
    }

    /**
     * Get all SellingMaterialGroup
     * @param {Boolean} refresh - to ignore storage
     * @returns {Array}
     */
    async getGroups(refresh = false) {
        let materialGroups = this.getArrayOnStorage(this.STORAGE_DEFINITION.MATERIAL_LIST);
        
        if (materialGroups.length == 0 || refresh) {
            materialGroups = [];
            const response = await this.api.executeRequest(`/groups`, {}, this.api.methods.GET);

            if (response.success() && response.getContent() != null) {
                for (let data of response.getContent()) {
                    materialGroups.push(new SellingMaterialGroup(data));
                }
            }

            this.saveArrayOnStorage(this.STORAGE_DEFINITION.MATERIAL_LIST, materialGroups);
        }

        return materialGroups;
    }

    /**
     * Gets group full details
     * @param {String} groupId
     * @param {Integer} userId
     * @returns {SellingMaterialGroup}
     */
    async getGroupDetail(groupId = null, userId = null) {
        let groupObject = null;
        const response = await this.api.executeRequest(`/group_detail/${groupId}`, { isFromUser: userId }, this.api.methods.GET);

        if (response.success() && response.getContent() != null) {
            groupObject = new SellingMaterialGroup(response.getContent());
        }

        return groupObject;
    }

    async saveGroup(payload) {
        const id = payload.id ?? '';
        return await this.api.executeRequest(`/group_save/${id}`, payload, this.api.methods.POST);
    }

    async deleteGroup(id) {
        return await this.api.executeRequest(`/group_remove/${id}`, {}, this.api.methods.DELETE);
    }

    async delete(id) {
        return await this.api.executeRequest(`/${id}`, {}, this.api.methods.DELETE);
    }

    /**
     * Saves Material by creating or updating
     * @param {Object} materialData
     * @returns {api/models/Response}
     */
    async save(materialData = {}) {
        const response = await this.api.executeRequest('/', materialData, this.api.methods.POST, {
            headers: {
                "Content-Type": "multipart/form-data",
            }
        });

        if (response.success() && response.getContent() != null) {
            const materialObject = new Material(response.getContent());
            response.setContent(materialObject);
        }
        
        return response;
    }

    /**
     * Tries to get resourse blob data
     * @param {integer} materialId 
     * @returns {blob|null}
     */
    async fetchDownloadData(materialId = null) {
        return await this.api.executeRequest(`download/${materialId}`, {}, this.api.methods.GET, {responseType: 'blob'});
    }

    /**
     * Gets all avaliable materials
     */
    async getAllMaterials() {
        let sellingMaterials = [];
        const response = await this.api.executeRequest(`/all`, {}, this.api.methods.GET);

        if (response.success() && response.getContent() != null) {
            for (let data of response.getContent()) {
                sellingMaterials.push(new SellingMaterial(data));
            }
        }

        return sellingMaterials;
    }

    /**
     * Checks if SellingMaterialGroup id was already bought by User's
     * @param {Integer} sellingMaterialGroupId
     * @returns {Boolean}
     */
    async isMySellingMaterialGroup(sellingMaterialGroupId = null) {
        const response = await this.api.executeRequest(`/group_was_bought/${sellingMaterialGroupId}`, {}, this.api.methods.GET);
        return response.success();
    }
}

export default new SellingMaterialService();
